import React, { useEffect, useRef, useState } from 'react';
import { IconArrowDropDown, IconArrowDropUp, IconCheck, IconClose } from 'lib/icons';
import { MenuListItem, MENU_PLACEMENT } from 'lib/menu';
import { TruncationTitleLogic } from 'lib/truncation';

import PropTypes from 'prop-types';
import {
  StyledButtonContainer,
  StyledDropdownContainer,
  StyledEditIcon,
  StyledEditIconWrapper,
  StyledErrorMessage,
  StyledIndeterminateProgressCircle,
  StyledInlineInputForDropdown,
  StyledMenu,
  StyledSaveCancelIconButton,
  StyledTextContainer,
  StyledTruncatedText,
} from '../styled';

const EditableDropdown = ({
  className = '',
  dataTestId = '',
  defaultText = 'None',
  errorMessage,
  hasError,
  id,
  isSaving,
  onSave,
  options,
  placeholder = 'Select',
  selectedOption,
  ...other
}) => {
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [internalSelectedOption, setInternalSelectedOption] = useState(selectedOption);
  const [initialValue, setInitialValue] = useState(selectedOption);
  const [internalHasError, setInternalHasError] = useState(hasError);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [areButtonsShown, setAreButtonsShown] = useState(false);

  const containerRef = useRef();

  /* Dropdown Menu functions */
  const targetRef = useRef();
  const dimensionRef = useRef();

  const handleOpen = () => {
    setInternalHasError(false);
    setAreButtonsShown(true);
    setIsMenuOpen(true);
  };
  const handleCloseOnOutsideClick = () => {
    setIsMenuOpen(false);
  };

  const handleSelect = (option) => {
    setInternalSelectedOption(option);
    setIsMenuOpen(false);
  };

  /* edit-in-place handler functions */
  const handleCancel = () => {
    setInternalSelectedOption(initialValue);
    setIsMenuOpen(false);
    setIsInEditMode(false);
    setInternalHasError(false);
  };

  const handleSave = (newOption) => {
    if (newOption.value) {
      onSave(newOption);
    } else {
      handleCancel();
    }
  };

  const handleSwitchToEditMode = () => {
    setIsInEditMode(true);
    setIsMenuOpen(false);
    waitForConditionalRender();
  };

  const waitForConditionalRender = () => {
    if (!targetRef.current) {
      window.requestAnimationFrame(waitForConditionalRender);
    } else {
      targetRef.current.focus();
    }
  };

  /* Keyboard and outside click Interactions */
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleCancel();
    }
  };

  /* watcher events */

  // this handles the triangle icon on the right
  const useOutsideClickEventListener = (callback, isMenuOpen) => {
    const handleClick = () => {
      if (isMenuOpen) {
        if (callback) {
          callback();
        }
      }
    };

    useEffect(() => {
      document.addEventListener('mousedown', handleClick);
      return () => {
        document.removeEventListener('mousedown', handleClick);
      };
    });
  };

  useOutsideClickEventListener(handleCloseOnOutsideClick, isMenuOpen);

  // resets values when selected option is changed
  useEffect(() => {
    setInternalSelectedOption(internalSelectedOption);
    setInitialValue(internalSelectedOption);
  }, [selectedOption]);

  // saving handlers
  useEffect(() => {
    if (!isSaving && !hasError) {
      setInternalHasError(false);
      setIsInEditMode(false);
      setInitialValue(selectedOption);
    }
    if (!isSaving && hasError) {
      setInternalHasError(true);
      handleSwitchToEditMode();
    }
  }, [isSaving, hasError, selectedOption]);

  return isInEditMode ? (
    <StyledDropdownContainer className={className} data-testid={dataTestId} ref={containerRef}>
      <StyledInlineInputForDropdown
        className={className ? `${className}-editable-dropdown` : undefined}
        dataTestId={dataTestId ? `${dataTestId}-editable-dropdown` : undefined}
        enableCustomValidation
        icon={
          isSaving ? (
            <StyledIndeterminateProgressCircle size="small" />
          ) : isMenuOpen ? (
            <IconArrowDropUp />
          ) : (
            <IconArrowDropDown />
          )
        }
        hasError={internalHasError}
        hideLabel
        id={id}
        isDisabled={isSaving || false}
        $isSaving={isSaving}
        label=""
        labelRef={dimensionRef}
        name={`Editable Dropdown ${id}`}
        onChange={() => {}}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        ref={targetRef}
        value={internalSelectedOption.label}
        {...other}
      />
      <StyledMenu
        dataTestId={dataTestId ? `${dataTestId}-menu` : undefined}
        dimensionRef={dimensionRef}
        isAuto={false}
        labelRef={targetRef}
        onOpen={handleOpen}
        placement={MENU_PLACEMENT.BOTTOM_START}
      >
        {options.map((option, ind) => (
          <MenuListItem
            dataTestId={dataTestId ? `${dataTestId}-menu-item-${ind}` : undefined}
            isDisabled={option.disabled}
            isSelected={option.value === internalSelectedOption.value}
            key={ind}
            onClick={() => handleSelect(option)}
            hideSelectedItemCheckmark={false}
          >
            {option.label}
          </MenuListItem>
        ))}
      </StyledMenu>
      {internalHasError && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
      <StyledButtonContainer
        $areButtonsShown={areButtonsShown}
        className={className ? `${className}-button-container` : undefined}
        dataTestId={dataTestId ? `${dataTestId}-button-container` : undefined}
        id={`${id}-button-container`}
        $isSaving={isSaving}
      >
        <StyledSaveCancelIconButton
          className={className ? `${className}-save-cancel-button` : undefined}
          dataTestId={dataTestId ? `${dataTestId}-save-button` : undefined}
          icon={<IconCheck />}
          iconSize={16}
          id={`${id}-save-button`}
          onClick={() => handleSave(internalSelectedOption)}
          text="Button"
        />
        <StyledSaveCancelIconButton
          className={className ? `${className}-save-cancel-button` : undefined}
          dataTestId={dataTestId ? `${dataTestId}-cancel-button` : undefined}
          icon={<IconClose />}
          iconSize={16}
          id={`${id}-cancel-button`}
          onClick={handleCancel}
          text="Button"
        />
      </StyledButtonContainer>
    </StyledDropdownContainer>
  ) : (
    <>
      <TruncationTitleLogic />
      <StyledTextContainer
        className={className ? `${className}-editable-dropdown-container` : undefined}
        data-testid={dataTestId ? `${dataTestId}-editable-dropdown-container` : undefined}
        id={`${id}-editable-dropdown-container`}
        onClick={handleSwitchToEditMode}
        title="Click to edit"
      >
        <StyledTruncatedText>{internalSelectedOption.label || defaultText}</StyledTruncatedText>
        <StyledEditIconWrapper>
          <StyledEditIcon
            className={className ? `${className}-pencil-icon` : undefined}
            data-testid={dataTestId ? `${dataTestId}-pencil-icon` : undefined}
            height={16}
            id={`${id}-pencil-icon`}
            width={16}
          />
        </StyledEditIconWrapper>
      </StyledTextContainer>
    </>
  );
};

EditableDropdown.propTypes = {
  /** Adds additional class to the component for styling */
  className: PropTypes.string,
  /** id for testing */
  dataTestId: PropTypes.string,
  /** override 'None' as default text */
  defaultText: PropTypes.string,
  /** an error message to display when value is in an error state */
  errorMessage: PropTypes.string,
  /** handles whether the value being saved is in an error state */
  hasError: PropTypes.bool.isRequired,
  /** Unique EditableText identifier  */
  id: PropTypes.string.isRequired,
  /** handles whether the save function is complete  */
  isSaving: PropTypes.bool.isRequired,
  /** callback function called upon save */
  onSave: PropTypes.func.isRequired,
  /** menu options array */
  options: PropTypes.array.isRequired,
  /** Override default 'Select' placeholder */
  placeholder: PropTypes.string,
  /** the selected option */
  selectedOption: PropTypes.object.isRequired,
};

export { EditableDropdown };
